/** Shared plumbing for cubicle records — subscribe once, list-refresh,
 *  optimistic mutators. Every cubicle-specific component wraps this. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { subscribeWs, unsubscribeWs, useWebSocket } from "@/hooks/useWebSocket";
import { toast } from "sonner";

export default function useCubicleRecords(projectId, cubicleKey, kindFilter) {
  const [items, setItems] = useState([]);
  const [busy, setBusy]   = useState(false);

  const load = useCallback(async () => {
    if (!projectId || !cubicleKey) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/projects/${projectId}/cubicles/${cubicleKey}/records`,
        { params: kindFilter ? { kind: kindFilter } : {} },
      );
      setItems(data.items || []);
    } catch (e) {
      // Access-denied → show empty; other cubicles handle their own toasts.
      if (e?.response?.status !== 403) {
        console.warn("[cubicle records]", e?.message);
      }
      setItems([]);
    } finally { setBusy(false); }
  }, [projectId, cubicleKey, kindFilter]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    const ch = `cubicle:${projectId}:${cubicleKey}`;
    subscribeWs([ch]);
    return () => { unsubscribeWs([ch]); };
  }, [projectId, cubicleKey]);

  useWebSocket((evt) => {
    if (!evt?.type?.startsWith("cubicle.record_")) return;
    void load();
  });

  const create = async (kind, data) => {
    try {
      await api.post(
        `/projects/${projectId}/cubicles/${cubicleKey}/records`,
        { kind, data },
      );
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };
  const patch = async (recordId, dataPatch) => {
    try {
      await api.patch(
        `/projects/${projectId}/cubicles/${cubicleKey}/records/${recordId}`,
        { data_patch: dataPatch },
      );
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const remove = async (recordId) => {
    try {
      await api.delete(
        `/projects/${projectId}/cubicles/${cubicleKey}/records/${recordId}`,
      );
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return { items, busy, refresh: load, create, patch, remove };
}
